import React, { useEffect, useState } from "react";
import Button from "../../../components/Button";
import { FileText, Trash2, Download } from "lucide-react";
import { getResumes, deleteResume, apiUrl } from "../../../api";
import { confirmDialog } from "../../../utils/confirm";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

const AdminResumes = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try {
      const list = await getResumes();
      setResumes(list);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load resumes",
      );
      setResumes([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fileUrl = (r) => apiUrl(r.url);

  const handleDownload = async (r) => {
    try {
      const url = fileUrl(r);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = r.originalName || "resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: "Could not download the file. Try opening it manually.",
        confirmButtonColor: "#38915c",
      });
      // Fallback: open in new tab
      window.open(fileUrl(r), "_blank");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Delete resume?",
      text: "This will remove the resume file and record.",
    });
    if (!confirmed) return;
    try {
      await deleteResume(id);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Resume deleted successfully",
        confirmButtonColor: "#38915c",
      });
      await load(false);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to delete resume",
        confirmButtonColor: "#38915c",
      });
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary-600 mb-1">
            <FileText className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              Manage
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Resumes</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Uploaded resumes from the Career page.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={load}>
          Refresh
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Name
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Email
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Position
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  File
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Uploaded
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {resumes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No resumes uploaded yet.
                  </td>
                </tr>
              ) : (
                resumes.map((r) => (
                  <tr
                    key={r._id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 font-medium">{r.name}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {r.email}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {r.position || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {r.originalName}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleDownload(r)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          aria-label="Download Resume"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r._id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminResumes;
